import { useState, useSyncExternalStore } from 'react';
import { AlertTriangle, CloudOff, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { useLastSaved } from '../lib/offline';
import { discardOp, forceOp } from '../lib/offlineSync';
import { outbox, useOutbox, type OutboxOp } from '../lib/outbox';
import { useConnectionStatus } from '../lib/realtime';
import { Button, cx, Sheet } from './ui/ui';

/** Netz laut Browser (ergänzt den Zustand der Live-Verbindung) */
export function useNavigatorOnline(): boolean {
  return useSyncExternalStore(
    (cb) => {
      window.addEventListener('online', cb);
      window.addEventListener('offline', cb);
      return () => {
        window.removeEventListener('online', cb);
        window.removeEventListener('offline', cb);
      };
    },
    () => navigator.onLine !== false,
  );
}

const time = (at: number) => new Date(at).toLocaleString('de-DE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Offline-Hinweis (0.44): kleine Marke unten links; ein Tippen öffnet die Liste der ausstehenden
 * und nicht übernommenen Änderungen. Fehlt nichts und ist alles verbunden, ist nichts zu sehen.
 */
export function OfflineBar() {
  const ops = useOutbox();
  const navOnline = useNavigatorOnline();
  const conn = useConnectionStatus();
  const saved = useLastSaved();
  const [open, setOpen] = useState(false);
  const offline = !navOnline || conn === 'offline';
  const pending = ops.filter((o) => o.status === 'pending').length;
  const problems = ops.filter((o) => o.status !== 'pending').length;
  if (!offline && !ops.length) return null;

  const [Icon, text, tone] = problems
    ? [AlertTriangle, `${problems} nicht übernommen`, 'bg-rose-700 text-white']
    : offline
      ? [CloudOff, pending ? `Offline · ${pending} ausstehend` : 'Offline', 'bg-amber-200 text-amber-950 dark:bg-amber-800 dark:text-amber-50']
      : [Loader2, `Überträgt ${pending} …`, 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'];

  return (
    <>
      <div className="pointer-events-none fixed bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] left-3 z-[65] sm:bottom-6 sm:left-6">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cx('pointer-events-auto inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-medium shadow-lg', tone)}
          aria-label={`${text} – Details`}
        >
          <Icon size={16} className={Icon === Loader2 ? 'animate-spin' : undefined} aria-hidden />
          {text}
        </button>
      </div>
      <Sheet open={open} onOpenChange={setOpen} title="Offline und ausstehende Änderungen" side="center">
        <div className="flex flex-col gap-3 p-4 text-sm sm:p-5">
          {offline ? (
            <p>
              Du bist offline{saved ? <> – du siehst den Stand vom {time(saved)}</> : null}. Neue Karten, Verschieben, Archivieren, Titel, Beschreibung, Termine, Priorität,
              Blockiert, Erledigt, eigene Felder, Labels, Zuständige, die Checkliste und Kommentare ohne Dateien werden auf diesem Gerät gespeichert und übertragen, sobald du
              wieder online bist. Dateien, Abhängigkeiten und Einstellungen gehen erst wieder mit Verbindung.
            </p>
          ) : pending > 0 ? (
            <p>Du bist online. Ausstehende Änderungen werden jetzt übertragen.</p>
          ) : problems > 0 ? (
            <p>Diese offline gespeicherten Änderungen wurden nicht übernommen. Entscheide je Änderung: deine Fassung übernehmen oder verwerfen. Nichts davon wird ohne dich überschrieben.</p>
          ) : null}
          {ops.length > 0 ? (
            <ul className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]" aria-label="Änderungen">
              {ops.map((op) => (
                <OpRow key={op.id} op={op} />
              ))}
            </ul>
          ) : (
            <p className="text-[var(--ink-soft)]">Nichts ausstehend.</p>
          )}
          {pending > 0 && !offline && (
            <Button className="self-start" onClick={() => void outbox().flush()}>
              <RotateCcw size={15} aria-hidden /> Jetzt übertragen
            </Button>
          )}
        </div>
      </Sheet>
    </>
  );
}

function OpRow({ op }: { op: OutboxOp }) {
  const [busy, setBusy] = useState(false);
  const run = (fn: () => Promise<void>) => {
    setBusy(true);
    void fn().finally(() => setBusy(false));
  };
  return (
    <li className="flex flex-col gap-1.5 p-3">
      <span className="font-medium break-words">{op.label}</span>
      <span className={cx('text-xs', op.status === 'pending' ? 'text-[var(--ink-soft)]' : 'text-rose-700 dark:text-rose-300')}>
        {op.status === 'pending' ? `Wartet seit ${time(op.createdAt)}` : `Nicht übernommen: ${op.message ?? 'Fehler'}`}
      </span>
      <span className="flex flex-wrap gap-2">
        {op.status === 'conflict' && !op.message?.startsWith('Eine frühere') && (
          <Button size="sm" variant="primary" disabled={busy} onClick={() => run(() => forceOp(op))}>
            Meine Änderung trotzdem übernehmen
          </Button>
        )}
        <Button
          size="sm"
          disabled={busy}
          onClick={() => {
            if (window.confirm('Diese Änderung verwerfen? Sie wird nicht übertragen.')) run(() => discardOp(op));
          }}
        >
          <Trash2 size={14} aria-hidden /> Verwerfen
        </Button>
      </span>
    </li>
  );
}
